import { useLayoutEffect, useMemo, useState, type RefObject } from "react";

const getRect = (element: Element | null) => {
  if (!element) return null;
  return element.getBoundingClientRect();
};

const useRect = <T extends Element>(ref: RefObject<T | null>) => {
  const [rect, setRect] = useState<DOMRect | null>(null);

  const observer = useMemo(() => {
    if (typeof ResizeObserver === "undefined") return null;

    return new ResizeObserver(() => {
      setRect(getRect(ref.current));
    });
  }, []);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = () => setRect(getRect(element));

    update();
    observer?.observe(element);

    const win = element.ownerDocument?.defaultView;
    win?.addEventListener("resize", update);
    win?.addEventListener("scroll", update, true);

    return () => {
      observer?.disconnect();
      win?.removeEventListener("resize", update);
      win?.removeEventListener("scroll", update, true);
    };
  }, [ref.current, observer]);

  return rect;
};

export { useRect };
